import React, { useState, useEffect } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Select from '@radix-ui/react-select'
import { X, Settings as SettingsIcon, Monitor, Globe, Check, ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import userSettings, { ThemeType, LanguageType } from '../../utils/UserSettings'
import AISettings from './AISettings'
import './styles.css'

interface SettingsProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type SettingSection = 'general' | 'editor' | 'ai' | 'about'

const Settings: React.FC<SettingsProps> = ({ open, onOpenChange }) => {
  const [activeSection, setActiveSection] = useState<SettingSection>('general')
  const [theme, setTheme] = useState<ThemeType>(userSettings.getTheme())
  const [language, setLanguage] = useState<LanguageType>(userSettings.getLanguage())

  // 监听设置变化
  useEffect(() => {
    const removeListener = userSettings.addListener(() => {
      setTheme(userSettings.getTheme());
      setLanguage(userSettings.getLanguage());
    });

    return () => removeListener();
  }, []);

  // 处理主题变更
  const handleThemeChange = (newTheme: ThemeType) => {
    setTheme(newTheme);
    userSettings.setTheme(newTheme);
  };

  // 处理语言变更
  const handleLanguageChange = (newLanguage: LanguageType) => {
    setLanguage(newLanguage);
    userSettings.setLanguage(newLanguage);
  };

  const sections = [
    { id: 'general', label: '通用', icon: <Monitor className="w-4 h-4" /> },
    { id: 'editor', label: '编辑器', icon: <SettingsIcon className="w-4 h-4" /> },
    { id: 'ai', label: 'AI设置', icon: <Sparkles className="w-4 h-4" /> },
    { id: 'about', label: '关于', icon: <Globe className="w-4 h-4" /> }
  ]
  
  // 主题选项数据
  const themeOptions = [
    { value: 'system', label: '跟随系统' },
    { value: 'light', label: '浅色' },
    { value: 'dark', label: '深色' }
  ]
  
  // 语言选项数据
  const languageOptions = [
    { value: 'zh', label: '简体中文' },
    { value: 'en', label: 'English' }
  ]

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 dialog-overlay" />
        <Dialog.Content className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] 
          w-[800px] h-[600px] rounded-lg shadow-lg
          dialog-content flex overflow-hidden focus:outline-none">
          
          <Dialog.Title className="sr-only">设置</Dialog.Title>
          <Dialog.Description className="sr-only">
            在这里可以调整应用程序的通用设置、编辑器设置、AI设置和查看关于信息
          </Dialog.Description>
          
          {/* 左侧边栏 */}
          <div className="settings-sidebar space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`settings-button ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id as SettingSection)}
              >
                {section.icon}
                <span>{section.label}</span>
              </button>
            ))}
          </div>

          {/* 右侧内容区 */}
          <div className="settings-content">
            {activeSection === 'general' && (
              <div className="space-y-6">
                <div>
                  <h2 className="settings-heading">通用设置</h2>
                  
                  {/* 主题设置 */}
                  <div className="space-y-4">
                    <div className="settings-section">
                      <label className="settings-label">主题</label>
                      <Select.Root value={theme} onValueChange={handleThemeChange}>
                        <Select.Trigger className="settings-select-trigger">
                          <Select.Value />
                          <Select.Icon>
                            <ChevronDown className="w-4 h-4 settings-select-icon" />
                          </Select.Icon>
                        </Select.Trigger>

                        <Select.Portal>
                          <Select.Content className="rounded-lg shadow-lg border overflow-hidden z-50">
                            <Select.ScrollUpButton className="flex items-center justify-center h-6 cursor-default">
                              <ChevronUp className="w-4 h-4" />
                            </Select.ScrollUpButton>
                            
                            <Select.Viewport>
                              {themeOptions.map(option => (
                                <Select.Item 
                                  key={option.value}
                                  value={option.value} 
                                  className="flex items-center px-3 py-2 text-sm cursor-pointer outline-none relative"
                                >
                                  <Select.ItemText>{option.label}</Select.ItemText>
                                  <Select.ItemIndicator className="absolute right-3">
                                    <Check className="w-4 h-4" />
                                  </Select.ItemIndicator>
                                </Select.Item>
                              ))}
                            </Select.Viewport>

                            <Select.ScrollDownButton className="flex items-center justify-center h-6 cursor-default">
                              <ChevronDown className="w-4 h-4" />
                            </Select.ScrollDownButton>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                    </div>

                    {/* 语言设置 */}
                    <div className="settings-section">
                      <label className="settings-label">语言</label>
                      <Select.Root value={language} onValueChange={handleLanguageChange}>
                        <Select.Trigger className="settings-select-trigger">
                          <Select.Value />
                          <Select.Icon>
                            <ChevronDown className="w-4 h-4 settings-select-icon" />
                          </Select.Icon>
                        </Select.Trigger>

                        <Select.Portal>
                          <Select.Content className="rounded-lg shadow-lg border overflow-hidden z-50">
                            <Select.ScrollUpButton className="flex items-center justify-center h-6 cursor-default">
                              <ChevronUp className="w-4 h-4" />
                            </Select.ScrollUpButton>
                            
                            <Select.Viewport>
                              {languageOptions.map(option => (
                                <Select.Item 
                                  key={option.value}
                                  value={option.value}
                                  className="flex items-center px-3 py-2 text-sm cursor-pointer outline-none relative"
                                >
                                  <Select.ItemText>{option.label}</Select.ItemText>
                                  <Select.ItemIndicator className="absolute right-3">
                                    <Check className="w-4 h-4" />
                                  </Select.ItemIndicator>
                                </Select.Item>
                              ))}
                            </Select.Viewport>

                            <Select.ScrollDownButton className="flex items-center justify-center h-6 cursor-default">
                              <ChevronDown className="w-4 h-4" />
                            </Select.ScrollDownButton>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeSection === 'editor' && <div>编辑器设置（待实现）</div>}
            {activeSection === 'ai' && <AISettings />}
            {activeSection === 'about' && <div>关于（待实现）</div>}
          </div>

          {/* 关闭按钮 */}
          <Dialog.Close className="settings-close-button">
            <X className="w-4 h-4" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default Settings 